import { useState } from "react";

/**
 * This is an enhanced useState hook which keeps track of the current state inside the
 * browser's sessionStorage. This allows for keeping track of some values during the
 * duration of a user's browsing session. It's useful to avoid showing back informative
 * messages twice during a browsing session.
 *
 * @param {String} key The key used to fetch and store the state value in sessionStorage
 * @param {any} defaultValue The default value if there's nothing stored in sessionStorage
 *   for that key
 * @param {{ deserialize: Function }} options Can be provided a `deserialize` function to
 *   cast the stored value into a usable value. This is needed because sessionStorage
 *   only stores string values.
 * @returns An array containing the current state value and its setter, just like the
 *   `useState` hook.
 *
 */
export const useSessionState = (key, defaultValue, options = {}) => {
  const storedValue = sessionStorage.getItem(key);
  const deserialize = options.deserialize || (value => value);
  const startValue = storedValue ? deserialize(storedValue) : defaultValue;

  const [value, setValue] = useState(startValue);

  const updateValue = newValue => {
    sessionStorage.setItem(key, newValue);
    setValue(newValue);
  };

  return [value, updateValue];
};
